import { ContentfulRichTextBlock } from 'libs/contentful/props';
import { Text } from '@contentful/rich-text-types';
import { Colorways } from 'types/constants';
import { ReactNode } from 'react';
import { CustomQueryStringParameterItems } from 'libs/contentful/props';

export const EMAIL_REGEX =
  /^([a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+(\.[a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+)*|"((([ \t]*\r\n)?[ \t]+)?([\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*(([ \t]*\r\n)?[ \t]+)?")@(([a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.)+([a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.?$/i;

export const getIdFromText = (text: string) => {
  if (!text) return '';

  text = text.toLowerCase().replace(/[^a-z0-9-]/g, '-');

  return encodeURIComponent(text);
};

export const getTextFromChildren = (children: ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') {
    return children.toString();
  }

  if (Array.isArray(children)) {
    return children.map(getTextFromChildren).join('');
  }

  if (typeof children === 'object' && children && 'props' in children) {
    return getTextFromChildren((children as { props: { children: ReactNode } }).props.children);
  }

  return '';
};

export const getCleanDocument = (document: Document | object): Document | object => {
  if (document && typeof document === 'object' && 'json' in document) {
    return document.json as Document;
  }

  return document;
};

const extractTextFromNode = (node: unknown): string => {
  if (!node || typeof node !== 'object') {
    return '';
  }

  // If the node has a value property, it's a text node
  if ('value' in node && typeof node.value === 'string') {
    return node.value;
  }

  // If the node has content, recursively process all child nodes
  if ('content' in node && Array.isArray(node.content)) {
    return node.content.map(extractTextFromNode).join('');
  }

  return '';
};

export const getRichTextValue = (document: Document | object): string => {
  if (document && typeof document === 'object' && 'json' in document) {
    return extractTextFromNode(document.json);
  }

  if (typeof document === 'string') {
    return document;
  }

  return '';
};

export const getColorwayName = (colorway: { name: Colorways } | Colorways | null | undefined): Colorways => {
  if (!colorway) {
    return Colorways.Primary;
  }

  if (typeof colorway === 'object' && 'name' in colorway) {
    return colorway.name;
  }

  return colorway;
};

export const pascalToSnake = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // Insert an underscore before uppercase letters
    .toLowerCase(); // Convert the whole string to lowercase
};

export const pascalToCamel = (str: string) => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

export const titleToCamel = (str: string) => {
  return str.replace(/\s+/g, '').charAt(0).toLowerCase() + str.slice(1);
};

export const toSnakeCase = (str: string) => {
  return str.replace(/\W+/g, ' ').trim().toLowerCase().replace(/\s/g, '_');
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatDate = (str: string) => {
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  };
  const parsedDate = Date.parse(str);
  return new Date(parsedDate).toLocaleDateString('en-US', opts);
};

export const convertCustomQspsToObject = (
  collectionItems: CustomQueryStringParameterItems[],
): Record<string, string> => {
  if (!collectionItems || collectionItems.length === 0) {
    return {};
  }

  return collectionItems.reduce(
    (acc, item) => {
      if (item.key && item.value) {
        acc[item.key] = item.value;
      }
      return acc;
    },
    {} as Record<string, string>,
  );
};
